import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Avatar, Box, Button, Divider, Flex, Text, VStack } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Key } from 'phosphor-react';
import { InputForm } from '../../inputs/forms/InputForm';
import { IProfileUpdate, useProfile } from '../../../hooks/useProfile';
import { useAuth } from '../../../providers/auth';

const schema = yup.object().shape({
  nova_senha: yup.string().optional(),
  confirmar_senha: yup.string().when('nova_senha', {
    is: (value: string | undefined) => !!value,
    then: schema2 =>
      schema2
        .required('Confirme a senha')
        .oneOf([yup.ref('nova_senha')], 'As senhas não são iguais'),
    otherwise: schema2 => schema2.optional(),
  }),
});

interface IForm {
  nova_senha?: string;
  confirmar_senha?: string;
}

export default function ProfileForm() {
  const { getUser, user } = useAuth();
  const { updateProfile } = useProfile();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutateAsync } = useMutation({
    mutationFn: updateProfile,
  });

  const formMethods = useForm<IForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      nova_senha: '',
      confirmar_senha: '',
    },
  });

  const onSubmit = async (values: IForm) => {
    if (!values.nova_senha) {
      toast.error('Digite a nova senha para atualizar.', { position: 'top-right' });
      return;
    }

    setIsSubmitting(true);
    try {
      await mutateAsync({
        nova_senha: values.nova_senha,
      } as IProfileUpdate);

      await getUser();
      formMethods.reset();
      toast.success('Senha atualizada com sucesso!', {
        position: 'top-right',
      });
    } catch (error: any) {
      let errorMessage = 'Erro interno no sistema, tente novamente mais tarde.';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail[0]?.msg || errorMessage;
      }
      toast.error(errorMessage, { position: 'top-right' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="800px" mx="auto">
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <FormProvider {...formMethods}>
          <Box bg="white" borderRadius="xl" boxShadow="sm" overflow="hidden">
            {/* Header com Avatar */}
            <Box bg="gray.50" py={8} px={6}>
              <Flex direction="column" align="center" gap={3}>
                <Avatar
                  size="2xl"
                  name={typeof user?.nome === 'string' ? user.nome : 'Usuário'}
                  src={typeof user?.imagem_url === 'string' ? user.imagem_url : undefined}
                  bg="primary"
                  color="white"
                />
                <VStack spacing={0}>
                  <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                    {typeof user?.nome === 'string' ? user.nome : 'Seu Nome'}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {typeof user?.email === 'string' ? user.email : 'seu@email.com'}
                  </Text>
                  <Text fontSize="xs" color="blue.500" mt={1}>
                    {typeof user?.grupo?.nome === 'string' ? user.grupo.nome : 'Grupo'}
                  </Text>
                </VStack>
              </Flex>
            </Box>

            {/* Informações */}
            <Box p={6}>
              <VStack spacing={6} align="stretch">
                {/* Dados do Usuário - Somente visualização */}
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={4}>
                    INFORMAÇÕES DA CONTA
                  </Text>
                  <VStack spacing={3} align="stretch">
                    <Flex
                      justify="space-between"
                      align="center"
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                    >
                      <Text fontSize="sm" color="gray.500">
                        Nome
                      </Text>
                      <Text fontSize="sm" fontWeight="medium" color="gray.700">
                        {typeof user?.nome === 'string' ? user.nome : '-'}
                      </Text>
                    </Flex>
                    <Flex
                      justify="space-between"
                      align="center"
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                    >
                      <Text fontSize="sm" color="gray.500">
                        E-mail
                      </Text>
                      <Text fontSize="sm" fontWeight="medium" color="gray.700">
                        {typeof user?.email === 'string' ? user.email : '-'}
                      </Text>
                    </Flex>
                    <Flex
                      justify="space-between"
                      align="center"
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                    >
                      <Text fontSize="sm" color="gray.500">
                        Grupo
                      </Text>
                      <Text fontSize="sm" fontWeight="medium" color="gray.700">
                        {typeof user?.grupo?.nome === 'string' ? user.grupo.nome : '-'}
                      </Text>
                    </Flex>
                    <Flex
                      justify="space-between"
                      align="center"
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                    >
                      <Text fontSize="sm" color="gray.500">
                        Status
                      </Text>
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        color={user?.ativo === true ? 'green.500' : 'red.500'}
                      >
                        {user?.ativo === true ? 'Ativo' : 'Inativo'}
                      </Text>
                    </Flex>
                  </VStack>
                </Box>

                <Divider />

                {/* Alterar Senha */}
                <Box>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                        ALTERAR SENHA
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Digite a nova senha para atualizar
                      </Text>
                    </Box>
                    <Key size={24} color="#718096" />
                  </Flex>
                  <Flex gap={4} w="100%" direction={{ base: 'column', md: 'row' }}>
                    <Box flex={1}>
                      <InputForm
                        name="nova_senha"
                        label="Nova senha"
                        placeholder="Digite a nova senha"
                        type="password"
                      />
                    </Box>
                    <Box flex={1}>
                      <InputForm
                        name="confirmar_senha"
                        label="Confirmar senha"
                        placeholder="Confirme a nova senha"
                        type="password"
                      />
                    </Box>
                  </Flex>
                </Box>
              </VStack>
            </Box>

            {/* Footer com botões */}
            <Box px={6} py={4} bg="gray.50" borderTop="1px" borderColor="gray.100">
              <Flex justify="flex-end">
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  loadingText="Salvando..."
                >
                  Atualizar Senha
                </Button>
              </Flex>
            </Box>
          </Box>
        </FormProvider>
      </form>
    </Box>
  );
}
